import { useEffect, useRef, useState } from 'react';
import { setReducedMotion } from '@/components/feature/hairline/kernel';
import { mountApproval } from './approvalFigure';
import type { IntHandle, IntState } from './approvalFigure';
import '@/pages/services/components/service-visual.css';
import './int-approval.css';

/* Trace copy per *actual* step (reported by the figure; "Recorded" only after archive arrival). */
const TRACE: Record<IntState, [string, string, string, string, string]> = {
  pending: ['Pending / Human sign-off', 'Pending', 'Received', 'Awaiting review', 'Not recorded'],
  review: ['In review / Human sign-off', 'In review', 'Received', 'Reviewing', 'Awaiting sign-off'],
  approved: ['Approved / Recorded', 'Recorded', 'Received', 'Approved', 'Recorded'],
  changes: ['Needs changes / Human sign-off', 'Needs changes', 'Returned for changes', 'Changes requested', 'Not recorded'],
};

const BUTTONS: [IntState, string][] = [
  ['review', 'Review'],
  ['approved', 'Approve'],
  ['changes', 'Changes'],
];

/* INT Approval Gate — ported from INT_Approval_Gate v1 (no iframe).
   Human gate: the approved path is reachable ONLY through a click on "Approve & record".
   No autoplay, no hover, no timer, and the QA ?state= URL override is intentionally removed. */
export default function IntApproval() {
  const stageRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const handleRef = useRef<IntHandle | null>(null);
  const [phase, setPhase] = useState<IntState>('pending');

  useEffect(() => {
    const stage = stageRef.current;
    const svg = svgRef.current;
    if (!stage || !svg) return undefined;
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handle = mountApproval({ stage, svg, read: { textContent: 'pending' } }, 1);
    const onMedia = () => setReducedMotion(media.matches);
    onMedia();
    media.addEventListener('change', onMedia);
    handle.onState(setPhase);
    handleRef.current = handle;
    return () => {
      handleRef.current = null;
      media.removeEventListener('change', onMedia);
      handle.destroy();
    };
  }, []);

  const [header, tag, request, owner, decision] = TRACE[phase];

  return (
    <div className={`sv-root sv-int state-${phase}`} data-state={phase}>
      <div className="sv-feature">
        <section className="sv-editorial">
          <div>
            <div className="sv-kicker sv-mono"><b>Workflow</b> / Internal operations</div>
            <h3>A human decision.<br />Before the next step.</h3>
            <p className="sv-description">Assess the request, hold it at the approval checkpoint, and record the decision after an owner explicitly approves it.</p>
          </div>
          <div className="sv-benefits">
            <div className="sv-benefit"><span className="sv-n">01</span><span>Receive the request</span></div>
            <div className="sv-benefit"><span className="sv-n">02</span><span>Review with an owner</span></div>
            <div className="sv-benefit"><span className="sv-n">03</span><span>Record the decision</span></div>
          </div>
        </section>

        <section className="sv-system" aria-label="Interactive human approval gate">
          <header className="sv-system-top">
            <span className="sv-mono sv-kicker">System / Human sign-off</span>
            <span className="sv-live sv-mono"><i />{header}</span>
          </header>

          <div ref={stageRef} className="sv-figure-stage int-stage" data-controlled="true">
            <div className="int-figure">
              <svg
                ref={svgRef}
                className="int-svg"
                viewBox="0 60 400 200"
                role="img"
                aria-label="A request moves to a human signature board, pauses for sign-off, and then enters an approved archive folder."
              />
              <div className="int-captions">
                <div><small>01 / REQUEST</small><strong>Request</strong></div>
                <div>
                  <small>02 / SIGN OFF</small>
                  <strong>Human review</strong>
                  <span className="int-person">
                    <svg viewBox="0 0 20 20" aria-hidden="true"><circle cx="10" cy="6" r="3" /><path d="M4 17v-2a6 6 0 0 1 12 0v2" /></svg>
                    REVIEWER
                  </span>
                  <span className="int-review-note">Human sign-off required</span>
                </div>
                <div><small>03 / RECORD</small><strong>Approved archive</strong></div>
              </div>
            </div>
          </div>

          <div className="sv-readout" aria-live="polite">
            <div className="sv-readout-top sv-mono">
              <span>Trace / Representative request</span>
              <span className="sv-tag">{tag}</span>
            </div>
            <div className="sv-events">
              <div className="sv-event"><small>001 / REQUEST</small><span>{request}</span></div>
              <div className="sv-event"><small>002 / OWNER</small><span>{owner}</span></div>
              <div className="sv-event"><small>003 / DECISION</small><span className="sv-event-state">{decision}</span></div>
            </div>
          </div>

          <div className="sv-controls">
            <div className="sv-controls-caption sv-mono">Demo / Approval</div>
            <div className="sv-button-group" role="group" aria-label="Preview human approval">
              {BUTTONS.map(([s, label]) => (
                <button
                  key={s}
                  type="button"
                  className="sv-mode cursor-target"
                  aria-pressed={phase === s}
                  onClick={() => handleRef.current?.setState(s)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}